import type { RiskScoreSnapshotResponse } from "@/api";
import { useTranslation } from "@/i18n";
import { formatDateID } from "@/lib/format";
import { formatScore } from "@/lib/risk-helpers";
import { DIMENSION_KEY } from "@/components/risk/risk-dimension-keys";
import {
  Dialog,
  DialogBody,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ScoreBandBadge } from "@/components/shared/score-band-badge";
import { DeltaIndicator } from "@/components/shared/delta-indicator";

interface Props {
  snapshot: RiskScoreSnapshotResponse | null;
  onClose: () => void;
}

/**
 * Snapshot detail dialog. The backend doesn't expose a
 * ``GET /snapshots/{id}`` endpoint — every field needed for the
 * detail view already ships in the list row, so the dialog reads
 * directly from the passed snapshot prop without a second fetch.
 */
export function RiskSnapshotDetailDialog({ snapshot, onClose }: Props) {
  const { t } = useTranslation();
  const open = snapshot !== null;

  return (
    <Dialog open={open} onClose={onClose} size="max-w-xl">
      <DialogHeader>
        <DialogTitle>{t("risk.snapshotDetail.title")}</DialogTitle>
        <DialogDescription>
          {snapshot
            ? t("risk.snapshotDetail.subtitle", {
                time: formatDateID(snapshot.computed_at),
                type: snapshot.computation_type,
              })
            : ""}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        {snapshot && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Tile label={t("risk.snapshotDetail.overallScoreLabel")}>
                <div className="flex items-center gap-2">
                  <span className="text-2xl font-medium text-ocean-900">
                    {formatScore(snapshot.overall_score)}
                  </span>
                  <ScoreBandBadge band={snapshot.score_band} />
                </div>
              </Tile>
              <Tile label={t("risk.snapshotDetail.deltaLabel")}>
                <DeltaIndicator delta={snapshot.delta_overall} />
              </Tile>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <DimensionTile
                label={t(DIMENSION_KEY.security)}
                score={snapshot.security_score}
                delta={snapshot.delta_security}
              />
              <DimensionTile
                label={t(DIMENSION_KEY.policy)}
                score={snapshot.policy_score}
                delta={snapshot.delta_policy}
              />
              <DimensionTile
                label={t(DIMENSION_KEY.asset)}
                score={snapshot.asset_score}
                delta={snapshot.delta_asset}
              />
              <DimensionTile
                label={t(DIMENSION_KEY.compliance)}
                score={snapshot.compliance_score}
                delta={snapshot.delta_compliance}
              />
            </div>

            <div>
              <div className="label-eyebrow">
                {t("risk.snapshotDetail.evidenceLabel")}
              </div>
              <pre className="mt-1 max-h-60 overflow-auto rounded-md border border-ocean-100 bg-ocean-50/40 p-3 text-xs leading-snug text-ocean-900">
                {JSON.stringify(snapshot.signal_evidence ?? {}, null, 2)}
              </pre>
              <p className="mt-1 text-xs text-neutral-500">
                {t("risk.snapshotDetail.evidenceHint")}
              </p>
            </div>
          </div>
        )}
      </DialogBody>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>
          {t("risk.snapshotDetail.close")}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function Tile({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border border-ocean-100 bg-white p-3">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function DimensionTile({
  label,
  score,
  delta,
}: {
  label: string;
  score: string;
  delta: string | null;
}) {
  return (
    <div className="rounded-md border border-ocean-100 bg-white p-3">
      <div className="label-eyebrow">{label}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-lg font-medium text-ocean-900">
          {formatScore(score)}
        </span>
        <DeltaIndicator delta={delta} className="text-xs" />
      </div>
    </div>
  );
}
